import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { AveragePlayerScore } from '__generated__/globalTypes';
import { ConditionalWrapper } from 'atoms/layout/ConditionalWrapper';
import { Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { LabelXS } from 'atoms/typography';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { averageScoreCount } from 'lib/averagePlayerScore';
import { laptopAndAbove } from 'style/mediaQuery';

const Progress = styled.div`
  width: 100%;
  height: 2px;
  border-radius: var(--unit);
  background: var(--bg-color);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: calc(100% - var(--progress));
    background: var(--progress-color);
    border-radius: var(--double-unit);
  }
`;

const FixWidth = styled(Vertical).attrs({ center: true })`
  width: var(--triple-unit);
  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
  &.small {
    width: calc(var(--unit) * 4);
    @media ${laptopAndAbove} {
      width: calc(var(--unit) * 5);
    }
  }
  &.medium {
    width: calc(var(--unit) * 6);
  }
`;

const Label = styled(LabelXS)`
  .small & {
    font: var(--t-label-s);
    font-weight: bold;
  }
  .medium & {
    font: var(--t-headline-s);
    font-weight: bold;
  }
`;

type AllowedAveragePlayerScore =
  | typeof AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;

export type Props = {
  appearances: number;
  mode: AllowedAveragePlayerScore;
  size?: 'compact' | 'small' | 'medium';
  withTooltip?: boolean;
};

export const DumbPlayerAppearance = ({
  appearances,
  mode,
  withTooltip = false,
  size = 'compact',
}: Props) => {
  const { up: isLaptopOrAbove } = useScreenSize('laptop');

  return (
    <ConditionalWrapper
      wrap={withTooltip}
      Wrapper={(p: { children: ReactNode }) => (
        <Tooltip
          title={
            <FormattedMessage
              id="averagePlayerScoreCell.appearancesTooltip"
              defaultMessage="{appearances, number, percent} of games played over of the last {count}"
              values={{
                appearances,
                count: averageScoreCount[mode],
              }}
            />
          }
        >
          {p.children}
        </Tooltip>
      )}
    >
      <FixWidth gap={isLaptopOrAbove ? 0.5 : 0} className={size}>
        <Label color="var(--c-nd-600)">
          <FormattedNumber value={appearances} style="percent" />
        </Label>
        <Progress
          style={
            {
              '--bg-color': 'var(--c-nd-200)',
              '--progress-color': 'var(--c-blue-300)',
              '--progress': `${appearances * 100}%`,
            } as CSSProperties
          }
        />
      </FixWidth>
    </ConditionalWrapper>
  );
};
